import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from './ui/sonner';
import { contactAPI } from '../services/api';

const ContactForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    investmentType: '',
    investmentVolume: '',
    message: '',
    requestExpose: false,
    requestViewing: false,
    hasBonityProof: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await contactAPI.submitInquiry(formData);
      
      if (response.success) {
        toast.success(t('contact.successTitle'), {
          description: t('contact.successMessage')
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          investmentType: '',
          investmentVolume: '',
          message: '',
          requestExpose: false,
          requestViewing: false,
          hasBonityProof: false
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('contact.errorTitle'), {
        description: t('contact.errorMessage')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardContent className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name" className="text-white mb-2 block">{t('contact.name')} *</Label>
                <Input 
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  className="bg-white/10 border-white/30 text-white placeholder:text-slate-300"
                  placeholder={t('contact.namePlaceholder')}
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-white mb-2 block">{t('contact.email')} *</Label>
                <Input 
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  className="bg-white/10 border-white/30 text-white placeholder:text-slate-300"
                  placeholder={t('contact.emailPlaceholder')}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="phone" className="text-white mb-2 block">{t('contact.phone')}</Label>
                <Input 
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="bg-white/10 border-white/30 text-white placeholder:text-slate-300"
                  placeholder={t('contact.phonePlaceholder')}
                />
              </div>
              <div>
                <Label htmlFor="company" className="text-white mb-2 block">{t('contact.company')}</Label>
                <Input 
                  id="company"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="bg-white/10 border-white/30 text-white placeholder:text-slate-300"
                  placeholder={t('contact.companyPlaceholder')}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="investmentType" className="text-white mb-2 block">{t('contact.investmentType')} *</Label>
                <Select value={formData.investmentType} onValueChange={(value) => handleChange('investmentType', value)}>
                  <SelectTrigger className="bg-white/10 border-white/30 text-white">
                    <SelectValue placeholder={t('contact.pleaseSelect')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="acquisition">{t('contact.investmentTypes.acquisition')}</SelectItem>
                    <SelectItem value="hotel-only">{t('contact.investmentTypes.hotelOnly')}</SelectItem>
                    <SelectItem value="villa-only">{t('contact.investmentTypes.villaOnly')}</SelectItem>
                    <SelectItem value="development">{t('contact.investmentTypes.development')}</SelectItem>
                    <SelectItem value="partnership">{t('contact.investmentTypes.partnership')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="investmentVolume" className="text-white mb-2 block">{t('contact.investmentVolume')}</Label>
                <Select value={formData.investmentVolume} onValueChange={(value) => handleChange('investmentVolume', value)}>
                  <SelectTrigger className="bg-white/10 border-white/30 text-white">
                    <SelectValue placeholder={t('contact.pleaseSelect')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5-8m">5-8 {t('contact.million')} €</SelectItem>
                    <SelectItem value="8-12m">8-12 {t('contact.million')} €</SelectItem>
                    <SelectItem value="12-15m">12-15 {t('contact.million')} €</SelectItem>
                    <SelectItem value="15m+">15+ {t('contact.million')} €</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="message" className="text-white mb-2 block">{t('contact.message')}</Label>
              <Textarea 
                id="message"
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                rows={5}
                className="bg-white/10 border-white/30 text-white placeholder:text-slate-300"
                placeholder={t('contact.messagePlaceholder')}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Checkbox 
                  id="requestExpose"
                  checked={formData.requestExpose}
                  onCheckedChange={(checked) => handleChange('requestExpose', checked)}
                  className="border-white/30"
                />
                <Label htmlFor="requestExpose" className="text-white cursor-pointer">
                  {t('contact.requestExpose')}
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <Checkbox 
                  id="requestViewing"
                  checked={formData.requestViewing}
                  onCheckedChange={(checked) => handleChange('requestViewing', checked)}
                  className="border-white/30"
                />
                <Label htmlFor="requestViewing" className="text-white cursor-pointer">
                  {t('contact.requestViewing')}
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <Checkbox 
                  id="hasBonityProof"
                  checked={formData.hasBonityProof}
                  onCheckedChange={(checked) => handleChange('hasBonityProof', checked)}
                  className="border-white/30"
                />
                <Label htmlFor="hasBonityProof" className="text-white cursor-pointer">
                  {t('contact.hasBonityProof')}
                </Label>
              </div>
            </div>

            <div className="bg-white/10 border border-white/20 rounded-lg p-4">
              <p className="text-xs text-slate-200 leading-relaxed">
                {t('contact.privacyNotice')}
              </p>
            </div>

            <Button 
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#C9A961] hover:bg-[#D4AF37] text-white py-6 text-lg font-semibold"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('contact.sending')}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {t('contact.submit')}
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactForm;